import React, { useRef, useState, useEffect } from "react";
import { sendAudioToSTT } from "../../../services/api/tts.api";
import { useDispatch } from "react-redux";
import Button from "../../atoms/Button/Button";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import { handleCopy } from "../../../services/helpers/commonHelpers";
import { useTranslation } from "react-i18next";
import DictationOutput from "../../atoms/DictationOutput/DictationOutput";

const SpeechToTextPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recognitionRef = useRef<any>(null);
  const isRecordingRef = useRef(false);
  const finalTranscriptRef = useRef("");
  const stoppedListeningRef = useRef(false);

  const [finalTranscript, setFinalTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalDisplayedTranscript, setFinalDisplayedTranscript] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  const startRecording = async () => {
    setFinalDisplayedTranscript("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
      };

      mediaRecorder.start();

      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "sk";

      let finalDisplayText = "";

      recognition.onresult = (event: any) => {
        let interim = "";
        let finalText = "";

        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalText += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }

        if (stoppedListeningRef.current) {
          setFinalTranscript(finalTranscriptRef.current + finalText);
          setInterimTranscript(interim);
          finalDisplayText = finalTranscriptRef.current + finalText + interim;
        } else {
          setFinalTranscript(finalText);
          setInterimTranscript(interim);
          finalDisplayText = finalText + interim;
        }
      };

      recognition.onend = () => {
        console.log("ending", isRecordingRef.current);
        if (isRecordingRef.current) {
          stoppedListeningRef.current = true;
          finalTranscriptRef.current = `${finalDisplayText} `;

          recognition.start();
        } else {
          setFinalTranscript("");
          finalTranscriptRef.current = "";
          stoppedListeningRef.current = false;
          setInterimTranscript("");
        }
      };

      recognition.start();
      recognitionRef.current = recognition;

      setIsRecording(true);
      isRecordingRef.current = true;
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    setFinalDisplayedTranscript(
      (prev) => prev + finalTranscript + interimTranscript,
    );
    recognitionRef.current?.stop();
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    isRecordingRef.current = false;
  };

  const sendAudio = async () => {
    setFinalDisplayedTranscript("Analyzujem nahrávku, počkajte prosím...");

    if (!audioChunksRef.current.length) return;

    const audioBlob = new Blob(audioChunksRef.current, {
      type: "audio/webm",
    });

    const formData = new FormData();
    formData.append("file", audioBlob, "recording.webm");

    try {
      const response = await sendAudioToSTT(dispatch, formData);

      setFinalDisplayedTranscript(response.text);
    } catch (error) {
      setFinalDisplayedTranscript("Error sending audio to server");
    }
  };

  // const formatText = (text: string) => {
  //   return text;
  // const regex = /\b(subjekt[ií]vn[eéaá] |objekt[ií]vn[eéaá] )\b/gi;
  // const textWithNewLines = text
  //   .replace(regex, (match) => `\n\n${match.trim()}:\n`)
  //   .trimStart();
  // return textWithNewLines
  //   .split("\n")
  //   .map((line) => {
  //     if (!line) return line;
  //     return line.charAt(0).toUpperCase() + line.slice(1);
  //   })
  //   .join("\n");
  // };

  return (
    <div className="dui-stt">
      <div className="dui-stt-record-wrapper">
        <div className="dui-stt-title">{t("recordReport")}</div>
        <div className="dui-stt-record">
          {!isRecording ? (
            <Button
              icon={<MicIcon className="dui-record-button-icon" />}
              onClick={startRecording}
              corners="circle"
              appearance="info"
              className="dui-stt-record-button"
            />
          ) : (
            <div className="dui-record-button-recording">
              <Button
                icon={<StopIcon className="dui-record-button-icon" />}
                onClick={stopRecording}
                corners="circle"
                appearance="error"
                className="dui-stt-record-button dui-stt-record-button-pulsing"
              />
              <span className="dui-recording-timer">
                {Math.floor(recordingTime / 60)}:
                {(recordingTime % 60).toString().padStart(2, "0")}
              </span>
            </div>
          )}
          {audioURL && !isRecording && (
            <div className="dui-audio-player">
              <audio src={audioURL} controls />
              <Button onClick={sendAudio}>{t("analyzeAndSendReport")}</Button>
            </div>
          )}
        </div>
      </div>

      <div className="dui-stt-message">
        <div className="dui-stt-title">{t("doctorReportTitle")}</div>

        <div className="dui-stt-report">
          <DictationOutput
            finalDisplayedTranscript={finalDisplayedTranscript}
            finalTranscript={finalTranscript}
            interimTranscript={interimTranscript}
          />
          {finalDisplayedTranscript && (
            <Button
              className="dui-stt-copy-button"
              onClick={() => handleCopy(dispatch, finalDisplayedTranscript)}
            >
              {t("copy")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeechToTextPage;
